import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, MessageCircle, Upload, FileCheck2, Truck, Zap, Shield, Users, Ruler, MapPin, CheckCircle2, Layers, Droplets, Settings, Building2, Boxes, Cpu, Package,
} from "lucide-react";
import { trackHeroCTAClick } from "../lib/tracking";
import { resolveSite, services as staticServices, materials as staticMaterials, faqs as staticFaqs, portfolioItems as staticPortfolio } from "../lib/siteConfig";
import { usePublicData } from "../hooks/usePublicData";
import { useLanguage } from "../context/LanguageContext";
import SectionHeader from "../components/SectionHeader";
import CTAStrip from "../components/CTAStrip";
import { pub } from "../lib/pubThemeClasses";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { API_BASE } from "../lib/env";
import {
  pickLocalizedSetting,
  pickMetricValue,
  pickServiceDisplay,
  pickMaterialDisplay,
  pickPortfolioTitle,
  pickFaqDisplay,
} from "../lib/cmsI18n";

const iconMap = { Layers, Droplets, Zap, Settings, Building2, Boxes, Cpu, Ruler, Users, Package };

/** @param {string} s */
function splitHeroTitle(s) {
  const lines = String(s || "")
    .split(/\n/)
    .map((x) => x.trim())
    .filter(Boolean);
  return { l1: lines[0] || "", l2: lines[1] || "", l3: lines[2] || "" };
}

export default function Home() {
  const { settings, services, materials, portfolio, faqs } = usePublicData();
  const siteConfig = resolveSite(settings);
  const { t, language } = useLanguage();
  const SV = services?.length ? services : staticServices;
  const MA = materials?.length ? materials : staticMaterials.map((m) => ({ ...m, in_stock: true }));
  const PO = portfolio?.length ? portfolio : staticPortfolio;
  const FQ = faqs?.length ? faqs : staticFaqs;

  const heroEyebrow = pickLocalizedSetting(language, settings?.hero_eyebrow || t("home.heroEyebrow"), settings?.hero_eyebrow_en);
  const st = settings || {};
  const hasCmsHero =
    (st.hero_title_line1 && String(st.hero_title_line1).trim()) ||
    (st.hero_title_line2 && String(st.hero_title_line2).trim()) ||
    String(st.hero_title_line3 || "").trim();

  const i18nHero = splitHeroTitle(t("hero.title"));
  const heroL1 = hasCmsHero
    ? pickLocalizedSetting(language, st.hero_title_line1 || t("home.heroTitle1"), st.hero_title_line1_en)
    : i18nHero.l1;
  const heroL2 = hasCmsHero
    ? pickLocalizedSetting(language, st.hero_title_line2 || t("home.heroTitle2"), st.hero_title_line2_en)
    : i18nHero.l2;
  const heroL3 = hasCmsHero
    ? pickLocalizedSetting(language, st.hero_title_line3 ?? "", st.hero_title_line3_en ?? "")
    : i18nHero.l3;
  const heroSub = pickLocalizedSetting(language, st.hero_subtitle || t("hero.subtitle"), st.hero_subtitle_en);

  const metrics = useMemo(() => {
    if (settings?.metrics?.length) return settings.metrics;
    return [
      { k: "24sa", v: t("home.metrics.lead.label") },
      { k: "100+", v: t("home.metrics.materials.label") },
      { k: "1200+", v: t("home.metrics.projects.label") },
      { k: "81", v: t("home.metrics.shipping.label") },
    ];
  }, [settings?.metrics, t]);

  const hasCmsMetrics = Boolean(settings?.metrics?.length);

  const trustStrip = useMemo(
    () => [
      [FileCheck2, t("trust.instant")],
      [Zap, t("trust.fast")],
      [Truck, t("trust.delivery")],
      [Shield, t("trust.quality")],
    ],
    [t],
  );

  const howSteps = useMemo(
    () => [
      { n: "01", t: t("how.step1"), d: "", icon: Upload },
      { n: "02", t: t("how.step2"), d: "", icon: FileCheck2 },
      { n: "03", t: t("how.step3"), d: "", icon: Truck },
    ],
    [t],
  );

  const whyFeatures = useMemo(
    () => [
      { t: t("home.feature1.title"), d: t("home.feature1.desc"), icon: Zap },
      { t: t("home.feature2.title"), d: t("home.feature2.desc"), icon: Shield },
      { t: t("home.feature3.title"), d: t("home.feature3.desc"), icon: Ruler },
      { t: t("home.feature4.title"), d: t("home.feature4.desc"), icon: Users },
      { t: t("home.feature5.title"), d: t("home.feature5.desc"), icon: MapPin },
      { t: t("home.feature6.title"), d: t("home.feature6.desc"), icon: CheckCircle2 },
    ],
    [t],
  );

  const marqueeCues = useMemo(() => [t("home.marquee.1"), t("home.marquee.2"), t("home.marquee.3"), t("home.marquee.4")], [t]);

  const resolveImg = (p) => (p.storage_path ? `${API_BASE}/public/media/${p.storage_path}` : p.image);

  return (
    <div>
      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden bg-grain">
        <div className="absolute inset-0 grid-lines opacity-60 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#FF4F00]/10 blur-3xl pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-16 md:pt-28 pb-20 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 fade-up">
            <div className={`inline-flex items-center gap-3 border bg-neutral-950/[0.03] dark:bg-white/[0.02] px-4 py-2 mb-8 ${pub.borderSubtle}`}>
              <span className="w-2 h-2 bg-[#00FF9D] animate-pulse" />
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-soft">{heroEyebrow}</span>
            </div>
            <h1 className={`font-display text-5xl sm:text-6xl md:text-7xl lg:text-[92px] font-black uppercase tracking-[-0.02em] leading-[0.9] ${pub.heroHeading}`}>
              {heroL1}
              {heroL2 && String(heroL2).trim() ? (
                <>
                  <br />
                  <span className="text-[#FF4F00]">{heroL2}</span>
                </>
              ) : null}
              {heroL3 && String(heroL3).trim() ? (
                <>
                  <br />
                  {heroL3}
                </>
              ) : null}
            </h1>
            <p className="font-sans mt-8 text-base md:text-xl text-soft max-w-2xl leading-relaxed">{heroSub}</p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/teklif-al#teklif-yukle"
                data-testid="hero-quote-button"
                onClick={() => trackHeroCTAClick("primary", { language })}
                className="inline-flex items-center justify-between sm:justify-start gap-3 bg-[#FF4F00] hover:bg-[#E64700] text-black font-bold uppercase tracking-wider text-sm px-7 py-5 transition-all hover:-translate-y-0.5"
              >
                <Upload className="w-5 h-5" /> {t("cta.upload")} <ArrowUpRight className="w-5 h-5" />
              </Link>
              <Link
                to="/teklif-al#teklif-yukle"
                data-testid="hero-price-cta"
                onClick={() => trackHeroCTAClick("secondary", { language })}
                className={`inline-flex items-center justify-between sm:justify-start gap-3 font-bold uppercase tracking-wider text-sm px-7 py-5 transition-colors ${pub.btnGhost} ${pub.hoverText}`}
              >
                {t("cta.seePrice")} <ArrowUpRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap gap-x-7 gap-y-2.5" data-testid="hero-trust-strip">
              {trustStrip.map(([Icon, text]) => (
                <span key={text} className={`inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] ${pub.mutedStr}`}>
                  <Icon className="w-3 h-3 text-[#FF4F00] shrink-0" strokeWidth={2} />
                  {text}
                </span>
              ))}
            </div>

            <div className={`mt-10 grid grid-cols-2 md:grid-cols-4 border ${pub.gridHairline} ${pub.borderSubtle}`}>
              {metrics.map((s, i) => (
                <div key={`${s.k}-${i}`} className="bg-surface-2 p-5">
                  <div className={`font-sans text-3xl md:text-4xl font-extrabold tabular-nums ${pub.heroHeading}`}>{s.k}</div>
                  <div className="font-mono text-xs uppercase tracking-wide text-neutral-600 dark:text-[#a89f93] mt-2">
                    {hasCmsMetrics ? pickMetricValue(language, s) : s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4 fade-up" style={{ animationDelay: "150ms" }}>
            <div className={`relative border overflow-hidden group ${pub.borderSubtle}`}>
              <img
                src="https://images.pexels.com/photos/26645377/pexels-photo-26645377.jpeg"
                alt="3D Printer"
                className="w-full h-[360px] md:h-[520px] object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[rgba(10,10,11,0.98)] via-[rgba(10,10,11,0.32)] to-transparent" />
              <div className="absolute top-4 left-4 font-mono text-xs uppercase tracking-wide text-[#FF4F00] bg-[rgba(10,10,11,0.82)] px-3 py-1.5 border border-[#FF4F00]/30">
                {t("home.heroLiveBadge")}
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-mono text-xs uppercase tracking-wide text-soft mb-1">{t("home.heroCard.line1")}</div>
                <div className={`font-sans text-lg font-semibold ${pub.heroHeading}`}>{t("home.heroCard.line2")}</div>
                <div className="mt-3 h-1 rounded-full bg-neutral-200 dark:bg-white/15">
                  <div className="h-full rounded-full bg-[#FF4F00]" style={{ width: "78%" }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="how-it-works" className={`relative border-t ${pub.borderSubtle}`}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
          <SectionHeader eyebrow={t("how.eyebrow")} title={t("how.title")} description={t("how.description")} />
          <div className={`grid grid-cols-1 md:grid-cols-3 border ${pub.gridHairline} ${pub.borderSubtle}`}>
            {howSteps.map((s) => (
              <div key={s.n} className="bg-surface-1 p-8 md:p-12 group hover:bg-surface-2 transition-colors">
                <div className="flex items-center justify-between mb-10">
                  <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF4F00]">
                    {t("home.how.step")} {s.n}
                  </span>
                  <div className={`w-12 h-12 border grid place-items-center transition-colors group-hover:border-orange-500/80 dark:group-hover:border-orange-400/55 ${pub.borderSubtle}`}>
                    <s.icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                </div>
                <h3 className={`font-sans text-2xl md:text-3xl font-semibold tracking-tight mb-4 ${pub.heroHeading}`}>{s.t}</h3>
                {s.d ? <p className="font-sans text-sm text-soft leading-relaxed">{s.d}</p> : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" className={`relative border-t bg-surface-1 ${pub.borderSubtle}`}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
          <SectionHeader eyebrow={t("home.services.eyebrow")} title={t("home.services.title")} description={t("home.services.description")} />
          <div className={`grid grid-cols-1 md:grid-cols-6 border ${pub.gridHairline} ${pub.borderSubtle}`}>
            {SV.slice(0, 6).map((s, i) => {
              const Icon = iconMap[s.icon] || Layers;
              const span = i === 0 ? "md:col-span-4" : i === 5 ? "md:col-span-4" : "md:col-span-2";
              const sd = pickServiceDisplay(s, language);
              return (
                <Link key={s.key || s.id || i} to="/hizmetler" data-testid={`service-card-${s.key}`} className={`bg-surface-2 p-8 md:p-10 group hover:bg-surface-3 transition-colors ${span} relative overflow-hidden`}>
                  <div className="flex items-start justify-between mb-16">
                    <div className={`w-12 h-12 border grid place-items-center transition-colors group-hover:bg-[#FF4F00] ${pub.borderSubtle} group-hover:border-[#FF4F00]`}>
                      <Icon className="w-5 h-5 group-hover:text-black transition-colors" strokeWidth={1.5} />
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-neutral-500 dark:text-zinc-600 group-hover:text-[#FF4F00] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className={`font-sans text-2xl md:text-3xl font-semibold tracking-tight mb-3 ${pub.heroHeading}`}>{sd.title}</h3>
                  <p className="font-sans text-sm text-soft max-w-md">{sd.desc}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section data-testid="why-choose-us" className={`relative border-t ${pub.borderSubtle}`}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeader
              eyebrow={t("home.why.eyebrow", { brand: siteConfig.brand })}
              title={t("home.why.title")}
              description={t("home.why.desc")}
            />
            <Link to="/hakkimizda" className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-[#FF4F00] transition-colors ${pub.hoverText}`} data-testid="why-about-link">
              {t("home.why.more")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className={`lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 border ${pub.gridHairline} ${pub.borderSubtle}`}>
            {whyFeatures.map((f) => (
              <div key={f.t} className="bg-surface-2 p-7 group hover:bg-surface-3 transition-colors">
                <f.icon className="w-6 h-6 text-[#FF4F00] mb-5" strokeWidth={1.5} />
                <h4 className={`font-sans text-lg font-semibold tracking-tight mb-2 ${pub.heroHeading}`}>{f.t}</h4>
                <p className="font-sans text-sm text-soft leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MATERIALS PREVIEW */}
      <section data-testid="materials-preview" className={`relative border-t bg-surface-1 ${pub.borderSubtle}`}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <SectionHeader eyebrow={t("home.materials.eyebrow")} title={t("home.materials.title")} />
            <Link to="/malzemeler" data-testid="materials-all-link" className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-[#FF4F00] transition-colors ${pub.hoverText}`}>
              {t("home.materials.all")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border ${pub.gridHairline} ${pub.borderSubtle}`}>
            {MA.slice(0, 4).map((m, i) => {
              const md = pickMaterialDisplay(m, language);
              return (
              <div key={m.key || m.id || i} className="bg-surface-2 p-8 hover:bg-surface-3 transition-colors group">
                <div className="flex items-baseline justify-between mb-6">
                  <span className={`font-mono text-xs uppercase tracking-[0.25em] ${pub.mutedStr}`}>#{String(i + 1).padStart(2, "0")}</span>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${m.in_stock === false ? "text-amber-300" : "text-[#FF4F00]"}`}>
                    {m.in_stock === false ? t("home.material.inStock.no") : t("home.material.inStock.yes")}
                  </span>
                </div>
                <h4 className={`font-sans text-2xl md:text-3xl font-bold tracking-tight mb-3 ${pub.heroHeading} group-hover:text-[#FF4F00] transition-colors`}>{md.name}</h4>
                <p className="font-sans text-sm text-soft mb-6 leading-relaxed">{md.desc}</p>
                <div className={`pt-5 border-t font-mono text-xs uppercase tracking-wide text-neutral-600 dark:text-[#a89f93] ${pub.borderSubtle}`}>{m.spec}</div>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PORTFOLIO PREVIEW */}
      <section data-testid="portfolio-preview" className={`relative border-t ${pub.borderSubtle}`}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <SectionHeader eyebrow={t("home.portfolio.eyebrow")} title={t("home.portfolio.title")} />
            <Link to="/portfoy" data-testid="portfolio-all-link" className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-[#FF4F00] transition-colors ${pub.hoverText}`}>
              {t("home.portfolio.all")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {PO.slice(0, 4).map((p, i) => {
              const pTitle = pickPortfolioTitle(p, language);
              return (
              <div key={p.id || p.title} className={`relative group overflow-hidden border ${pub.borderSubtle} ${i === 0 ? "md:col-span-4 md:row-span-2" : "md:col-span-2"}`}>
                <img src={resolveImg(p)} alt={pTitle} className={`w-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ${i === 0 ? "h-[400px] md:h-[560px]" : "h-[270px]"}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-[rgba(10,10,11,0.98)] via-[rgba(10,10,11,0.24)] to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="font-mono text-xs uppercase tracking-wide text-[#FF4F00] mb-1">{p.category}</div>
                  <div className="font-sans text-lg md:text-xl font-semibold tracking-tight text-white">{pTitle}</div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRUST LOOP (marquee) */}
      <section data-testid="trust-marquee" className={`relative overflow-hidden border-t bg-surface-2 py-6 md:py-8 ${pub.borderSubtle}`} aria-hidden="true">
        {(() => {
          const Track = ({ dup }) => (
            <div className="flex shrink-0 gap-14 md:gap-20 items-center pr-14 md:pr-20">
              {marqueeCues.map((c) => (
                <span key={`${dup}-${c}`} className="font-mono text-[11px] md:text-xs uppercase tracking-[0.35em] text-neutral-600 dark:text-[#a89f93] whitespace-nowrap">
                  {c}
                </span>
              ))}
            </div>
          );
          return (
            <div className="animate-marquee flex w-max">
              <Track dup="a" />
              <Track dup="b" />
            </div>
          );
        })()}
      </section>

      {/* TRUST STRIP */}
      <section data-testid="trust-strip" className={`relative border-t bg-surface-2 ${pub.borderSubtle}`}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-20">
          <p className="font-sans text-sm text-neutral-600 dark:text-[#a89f93] mb-6 text-center max-w-2xl mx-auto leading-relaxed">{t("home.trustBlock", { brand: siteConfig.brand })}</p>
          <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-6 opacity-70">
            {["AETOS", "NOVARA", "KORYS", "MAKAI", "FORMLINE", "ATLAS", "IRONWORKS", "MECATRON"].map((b) => (
              <span key={b} className="font-sans font-semibold tracking-tight text-neutral-600 dark:text-[#8f877c] text-base md:text-lg">
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className={`relative border-t ${pub.borderSubtle}`}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow={t("home.faq.eyebrow")} title={t("home.faq.title")} description={t("home.faq.description")} />
            <a href={siteConfig.whatsappLink(t("faq.whatsapp.intro"))} target="_blank" rel="noreferrer" data-testid="faq-whatsapp-link" className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-[#FF4F00] transition-colors ${pub.hoverText}`}>
              <MessageCircle className="w-4 h-4" /> {t("home.faq.whatsappCta")}
            </a>
          </div>
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className={`border-t ${pub.borderSubtle}`} data-testid="faq-accordion">
              {FQ.map((f, i) => {
                const fq = pickFaqDisplay(f, language);
                return (
                <AccordionItem key={f.id || i} value={`item-${i}`} className={`border-b ${pub.borderSubtle}`}>
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className={`py-6 font-sans text-left text-lg md:text-xl font-semibold tracking-tight ${pub.heroHeading} hover:text-[#FF4F00] hover:no-underline`}>{fq.q}</AccordionTrigger>
                  <AccordionContent className="pb-6 text-sm md:text-base text-soft leading-relaxed">{fq.a}</AccordionContent>
                </AccordionItem>
              );
              })}
            </Accordion>
          </div>
        </div>
      </section>

      <CTAStrip />
    </div>
  );
}
